import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authService } from '@/services/auth';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Heart, Shield, ClipboardList } from 'lucide-react';

// Logo y decoración: Huella de perro simple y elegante
const PawPrint = ({ className, style, animated }: { className?: string; style?: React.CSSProperties; animated?: boolean }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} style={style} xmlns="http://www.w3.org/2000/svg">
    {animated ? (
      <>
        {/* Almohadilla central */}
        <ellipse cx="16" cy="22" rx="7" ry="6">
          <animate attributeName="ry" values="6;5.5;6" dur="2s" repeatCount="indefinite" />
        </ellipse>
        {/* Dedos con animación de "caminar" */}
        <ellipse cx="7" cy="12" rx="4" ry="4.5">
          <animate attributeName="cy" values="12;10;12" dur="1.5s" repeatCount="indefinite" begin="0s" />
        </ellipse>
        <ellipse cx="13" cy="6" rx="3.5" ry="4">
          <animate attributeName="cy" values="6;4.5;6" dur="1.5s" repeatCount="indefinite" begin="0.2s" />
        </ellipse>
        <ellipse cx="19" cy="6" rx="3.5" ry="4">
          <animate attributeName="cy" values="6;4.5;6" dur="1.5s" repeatCount="indefinite" begin="0.4s" />
        </ellipse>
        <ellipse cx="25" cy="12" rx="4" ry="4.5">
          <animate attributeName="cy" values="12;10;12" dur="1.5s" repeatCount="indefinite" begin="0.6s" />
        </ellipse>
      </>
    ) : (
      <>
        <ellipse cx="16" cy="22" rx="7" ry="6"/>
        <ellipse cx="7" cy="12" rx="4" ry="4.5"/>
        <ellipse cx="13" cy="6" rx="3.5" ry="4"/>
        <ellipse cx="19" cy="6" rx="3.5" ry="4"/>
        <ellipse cx="25" cy="12" rx="4" ry="4.5"/>
      </>
    )}
  </svg>
);

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'La contraseña es requerida'),
  rememberMe: z.boolean().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

const REMEMBERED_EMAIL_KEY = 'vet_remembered_email';

export default function Login() {
  const navigate = useNavigate();
  const { setAuth, setTenant } = useAuthStore();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      rememberMe: false,
    },
  });

  useEffect(() => {
    const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (rememberedEmail) {
      setValue('email', rememberedEmail);
      setValue('rememberMe', true);
    }
  }, [setValue]);

  const onSubmit = async (data: LoginForm) => {
    try {
      setLoading(true);
      setError('');

      if (data.rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, data.email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }

      const response = await authService.login(data.email, data.password);
      // Limpiar cualquier dato cacheado del usuario/veterinaria anterior
      queryClient.clear();
      setAuth(response.access_token, response.refresh_token, response.expires_in, response.user);

      // Superadmin no tiene tenant, redirigir directamente al panel admin
      if (response.user.role === 'superadmin') {
        navigate('/admin');
        return;
      }

      // Para usuarios normales, obtener tenant
      const tenant = await authService.getTenant(response.access_token);
      setTenant(tenant);

      // Usuarios de laboratorio van a /lab
      if (response.user.role === 'laboratory') {
        navigate('/lab');
        return;
      }

      // Usuarios normales van al dashboard de veterinaria
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-cream-50">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-14 h-14 bg-gradient-to-br from-taupe-500 to-taupe-700 rounded-2xl flex items-center justify-center shadow-lg">
              <PawPrint className="w-8 h-8 text-cream-100" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-taupe-800 tracking-tight">
                <span className="text-taupe-800">ANIMA</span><span className="text-taupe-500 font-light">vet</span>
              </h1>
              <p className="text-xs text-taupe-400 tracking-wide">GESTIÓN VETERINARIA</p>
            </div>
          </div>

          {/* Welcome text */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-taupe-800">Bienvenido</h2>
            <p className="mt-2 text-taupe-500">Ingresa a tu cuenta para continuar</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded-r-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-taupe-700 mb-1.5">
                  Correo electrónico
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="nombre@veterinaria.com"
                  autoComplete="email"
                  {...register('email')}
                  className="w-full px-4 py-3 bg-white border border-cream-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all text-taupe-800 placeholder-taupe-400"
                />
                {errors.email && (
                  <p className="mt-1.5 text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <Input
                id="password"
                type="password"
                label="Contraseña"
                placeholder="••••••••"
                autoComplete="current-password"
                {...register('password')}
                error={errors.password?.message}
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    {...register('rememberMe')}
                    className="peer sr-only"
                  />
                  <div className="w-5 h-5 border-2 border-taupe-300 rounded-md peer-checked:border-taupe-600 peer-checked:bg-taupe-600 transition-all"></div>
                  <svg
                    className="absolute top-0.5 left-0.5 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 transition-opacity"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-taupe-600 group-hover:text-taupe-800 transition-colors select-none">
                  Recordar usuario
                </span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-taupe-600 hover:text-taupe-800 font-medium"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full bg-taupe-700 hover:bg-taupe-800 text-cream-50 font-medium py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 mt-6"
              loading={loading}
            >
              Iniciar Sesión
            </Button>
          </form>

          <p className="mt-10 text-center text-sm text-taupe-400">
            © 2025 ANIMAvet. Todos los derechos reservados.
          </p>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex lg:w-[55%] bg-gradient-to-br from-taupe-600 via-taupe-700 to-taupe-800 relative overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cream-400/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cream-300/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3"></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-cream-500/15 rounded-full blur-3xl"></div>
        </div>

        {/* Huellas decorativas animadas */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <PawPrint animated className="absolute w-16 h-16 text-cream-300/15" style={{ top: '8%', left: '15%', transform: 'rotate(-25deg)' }} />
          <PawPrint animated className="absolute w-12 h-12 text-cream-300/12" style={{ top: '20%', right: '20%', transform: 'rotate(15deg)' }} />
          <PawPrint animated className="absolute w-20 h-20 text-cream-300/10" style={{ top: '45%', left: '8%', transform: 'rotate(-10deg)' }} />
          <PawPrint animated className="absolute w-14 h-14 text-cream-300/15" style={{ top: '35%', right: '10%', transform: 'rotate(30deg)' }} />
          <PawPrint animated className="absolute w-10 h-10 text-cream-300/12" style={{ bottom: '30%', left: '25%', transform: 'rotate(5deg)' }} />
          <PawPrint animated className="absolute w-18 h-18 text-cream-300/10" style={{ bottom: '15%', right: '25%', transform: 'rotate(-20deg)' }} />
          <PawPrint animated className="absolute w-8 h-8 text-cream-300/15" style={{ top: '60%', left: '45%', transform: 'rotate(45deg)' }} />
        </div>

        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center w-full p-16">
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cream-400/20 rounded-full mb-8 backdrop-blur-sm">
              <div className="w-2 h-2 bg-cream-300 rounded-full animate-pulse"></div>
              <span className="text-cream-200 text-sm font-medium">Sistema Activo</span>
            </div>

            <h2 className="text-4xl font-bold text-cream-50 leading-tight">
              Gestión integral para tu
              <span className="text-cream-300"> clínica veterinaria</span>
            </h2>

            <p className="mt-6 text-taupe-200 text-lg leading-relaxed">
              Controla inventario, gestiona pacientes y optimiza las ventas de tu veterinaria en una sola plataforma.
            </p>

            {/* Features */}
            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cream-400/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-cream-300" />
                </div>
                <div>
                  <h3 className="text-cream-50 font-semibold">Control de Stock</h3>
                  <p className="text-taupe-300 text-sm mt-1">Alertas automáticas de bajo stock y vencimientos próximos</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cream-400/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-cream-300" />
                </div>
                <div>
                  <h3 className="text-cream-50 font-semibold">Historial de Pacientes</h3>
                  <p className="text-taupe-300 text-sm mt-1">Fichas médicas completas de cada mascota</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cream-400/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="w-6 h-6 text-cream-300" />
                </div>
                <div>
                  <h3 className="text-cream-50 font-semibold">Comandas y Ventas</h3>
                  <p className="text-taupe-300 text-sm mt-1">Flujo de trabajo integrado para consultas y cobros</p>
                </div>
              </div>
            </div>
          </div>

          {/* Huella grande decorativa animada */}
          <div className="absolute bottom-12 right-12">
            <PawPrint animated className="w-48 h-48 text-cream-200/15" />
          </div>
        </div>
      </div>
    </div>
  );
}
